// src/components/LeadPopovers.jsx — Popover selectors for Status, Agent, Brand
import React, { useState, useEffect, useRef } from 'react';
import { Search, UserX, Check, Sparkles } from 'lucide-react';
import { STATUS_CONFIG } from './StatusBadge';

/**
 * Status Popover
 */
export function StatusPopover({ currentStatus, options, onSelect, onClose }) {
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="lead-popover" ref={popoverRef}>
      <div className="lead-popover__header">Change Status</div>
      <div className="lead-popover__list">
        {options.map((st) => {
          const cfg = STATUS_CONFIG[st] || { label: st, bg: '#334155', color: '#cbd5e1', dot: '#94a3b8' };
          const isSelected = st === currentStatus;
          return (
            <button
              key={st}
              type="button"
              className={`lead-popover__item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onSelect(st);
                onClose();
              }}
            >
              <span className="lead-popover__dot" style={{ backgroundColor: cfg.dot || cfg.color }} />
              <span className="lead-popover__label">{st}</span>
              {isSelected && <Check size={14} className="lead-popover__check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Agent Popover
 */
export function AgentPopover({ currentAgentId, agents, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const filtered = agents.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    (a.email && a.email.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="lead-popover" ref={popoverRef}>
      <div className="lead-popover__header">Assign Agent</div>
      <div className="lead-popover__search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search agent..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>
      <div className="lead-popover__list">
        <button
          type="button"
          className={`lead-popover__item ${!currentAgentId ? 'selected' : ''}`}
          onClick={() => {
            onSelect('', 'Unassigned');
            onClose();
          }}
        >
          <div className="lead-popover__avatar unassigned">
            <UserX size={13} />
          </div>
          <span className="lead-popover__label muted">Unassigned</span>
          {!currentAgentId && <Check size={14} className="lead-popover__check" />}
        </button>

        {filtered.map((ag) => {
          const agId = String(ag.id || ag._id);
          const isSelected = String(currentAgentId) === agId;
          const initials = ag.name
            ? ag.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
            : 'AG';
          return (
            <button
              key={agId}
              type="button"
              className={`lead-popover__item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onSelect(agId, ag.name);
                onClose();
              }}
            >
              <div className="lead-popover__avatar">
                {initials}
              </div>
              <div className="lead-popover__agent-info">
                <span className="lead-popover__label">{ag.name}</span>
                {ag.role && <span className="lead-popover__sub">{ag.role}</span>}
              </div>
              {isSelected && <Check size={14} className="lead-popover__check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Brand Popover
 */
export function BrandPopover({ currentBrandId, brands, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const filtered = brands.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="lead-popover" ref={popoverRef}>
      <div className="lead-popover__header">Select Brand</div>
      {brands.length > 5 && (
        <div className="lead-popover__search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search brand..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
      )}
      <div className="lead-popover__list">
        <button
          type="button"
          className={`lead-popover__item ${!currentBrandId ? 'selected' : ''}`}
          onClick={() => {
            onSelect('', 'No Brand');
            onClose();
          }}
        >
          <span className="lead-popover__label muted">None / No Brand</span>
          {!currentBrandId && <Check size={14} className="lead-popover__check" />}
        </button>

        {filtered.map((b) => {
          const bId = String(b.id || b._id);
          const isSelected = String(currentBrandId) === bId;
          return (
            <button
              key={bId}
              type="button"
              className={`lead-popover__item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onSelect(bId, b.name);
                onClose();
              }}
            >
              <Sparkles size={13} className="lead-popover__icon" />
              <span className="lead-popover__label">{b.name}</span>
              {isSelected && <Check size={14} className="lead-popover__check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
